import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminRole, AdminPermissions } from '@/types';

const PERMISSIONS_CACHE_KEY = 'user_permissions_cache';
const CACHE_EXPIRY_HOURS = 24;

interface CachedPermissions {
  permissions: AdminPermissions;
  timestamp: number;
}

export function usePermissions(role: AdminRole | null) {
  const [permissions, setPermissions] = useState<AdminPermissions | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPermissions();
  }, [role]);

  const getCachedPermissions = (): CachedPermissions | null => {
    const cached = localStorage.getItem(PERMISSIONS_CACHE_KEY);
    if (!cached) return null;

    const { permissions, timestamp } = JSON.parse(cached);
    const expiryTime = timestamp + (CACHE_EXPIRY_HOURS * 60 * 60 * 1000);

    if (Date.now() > expiryTime) {
      localStorage.removeItem(PERMISSIONS_CACHE_KEY);
      return null;
    }

    return { permissions, timestamp };
  };

  const setCachedPermissions = (permissions: AdminPermissions) => {
    const cacheData: CachedPermissions = {
      permissions,
      timestamp: Date.now()
    };
    localStorage.setItem(PERMISSIONS_CACHE_KEY, JSON.stringify(cacheData));
  };

  const loadPermissions = async () => {
    if (!role) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    try {
      // Check cache first
      const cached = getCachedPermissions();
      if (cached) {
        setPermissions(cached.permissions);
        setLoading(false);
        return;
      }

      // If no cache, fetch from database
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setPermissions(null);
        setLoading(false);
        return;
      }

      const { data: userPermissions } = await supabase
        .from('user_permissions')
        .select('permissions')
        .eq('user_id', user.id)
        .single();

      if (userPermissions) {
        setCachedPermissions(userPermissions.permissions);
        setPermissions(userPermissions.permissions);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  };

  const clearCache = () => {
    localStorage.removeItem(PERMISSIONS_CACHE_KEY);
    setPermissions(null);
  };

  return {
    permissions,
    loading,
    clearCache,
    refresh: loadPermissions
  };
}